import React, { useContext, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { LanguageContext } from "../context/LanguageContext";

const copy = {
  en: {
    title: "Jacques Gomes — AI Engineer | Multi-Agent Systems, RAG, LLMOps",
    description:
      "AI Engineer in Natal, Brazil. I design production multi-agent systems, agentic RAG, NL-to-SQL and LLMOps for enterprise data. Work includes Melow AI, Siemens Mobility, ChatADV and Louis Dreyfus.",
    ogLocale: "en_US",
  },
  pt: {
    title: "Jacques Gomes — Engenheiro de IA | Sistemas multi-agente, RAG, LLMOps",
    description:
      "Engenheiro de IA em Natal, Brasil. Projeto sistemas multi-agente, RAG agêntico, NL-to-SQL e LLMOps para dados empresariais. Trabalho inclui Melow AI, Siemens Mobility, ChatADV e Louis Dreyfus.",
    ogLocale: "pt_BR",
  },
};

const SITE = "https://jacquesgomes.com.br/";
const IMAGE = "https://jacquesgomes.com.br/og-jacques.jpg";

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Person",
      "@id": `${SITE}#person`,
      name: "Jacques Gomes",
      url: SITE,
      image: IMAGE,
      jobTitle: "AI Engineer",
      description:
        "AI Engineer specializing in multi-agent systems, agentic RAG, NL-to-SQL and LLMOps.",
      email: "mailto:jacquesgomes66@gmail.com",
      telephone: "+5584994514529",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Natal",
        addressRegion: "RN",
        addressCountry: "BR",
      },
      knowsAbout: [
        "Multi-Agent AI Systems",
        "Retrieval-Augmented Generation",
        "NL-to-SQL",
        "LLMOps",
        "Python",
        "Google Cloud Platform",
      ],
      worksFor: {
        "@type": "Organization",
        name: "Melow AI",
        url: "https://melow.ai",
      },
      sameAs: [
        "https://www.linkedin.com/in/jacques-gomes-627739240/",
        "https://github.com/JacquesGomes",
        "https://www.instagram.com/jacquesgomess/",
      ],
    },
    {
      "@type": "WebSite",
      "@id": `${SITE}#website`,
      url: SITE,
      name: "Jacques Gomes",
      inLanguage: ["en", "pt-BR"],
      publisher: { "@id": `${SITE}#person` },
    },
    {
      "@type": "WebPage",
      "@id": SITE,
      url: SITE,
      name: "Jacques Gomes — AI Engineer",
      isPartOf: { "@id": `${SITE}#website` },
      about: { "@id": `${SITE}#person` },
      primaryImageOfPage: IMAGE,
    },
  ],
};

const Seo = () => {
  const { language } = useContext(LanguageContext);
  const t = copy[language] || copy.en;

  useEffect(() => {
    document.documentElement.lang = language === "pt" ? "pt-BR" : "en";
  }, [language]);

  return (
    <Helmet>
      <html lang={language === "pt" ? "pt-BR" : "en"} />
      <title>{t.title}</title>
      <meta name="description" content={t.description} />
      <meta
        name="keywords"
        content="Jacques Gomes, AI Engineer, Engenheiro de IA, Multi-Agent Systems, Agentic RAG, NL-to-SQL, LLMOps, Python, GCP, Melow AI, Siemens Mobility"
      />
      <meta name="author" content="Jacques Gomes" />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <link rel="canonical" href={SITE} />
      <link rel="alternate" hrefLang="en" href={SITE} />
      <link rel="alternate" hrefLang="pt-BR" href={SITE} />
      <link rel="alternate" hrefLang="x-default" href={SITE} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Jacques Gomes" />
      <meta property="og:url" content={SITE} />
      <meta property="og:title" content={t.title} />
      <meta property="og:description" content={t.description} />
      <meta property="og:image" content={IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Jacques Gomes, AI Engineer" />
      <meta property="og:locale" content={t.ogLocale} />
      <meta property="og:locale:alternate" content={language === "pt" ? "en_US" : "pt_BR"} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t.title} />
      <meta name="twitter:description" content={t.description} />
      <meta name="twitter:image" content={IMAGE} />
      <meta name="twitter:image:alt" content="Jacques Gomes, AI Engineer" />

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export default Seo;
